import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TeamOutlined } from '@ant-design/icons';
import { Button, Tag } from 'antd';
import { ROUTES } from '@/constants';
import { FUNCTIONS, FUNCTION_COLORS } from '@/components/team-assignments/model';

const SHOWN = 12;

/**
 * The "HLB team" tile of the employee profile: the clients this person works
 * on, by type of work, with their hand-overs and a link to their view of the
 * board. Same data as TeamPersonSummary on the old view page (team-assignments
 * person summary); the caller loads it and shows the tile only for HLB staff
 * looking at a team member.
 */
export default function EmployeeTeamPanel({ summary }) {
  const { t } = useTranslation('teamAssignments');
  const [all, setAll] = useState(false);
  if (!summary) return null;

  const companies = summary.companies || [];
  const list = all ? companies : companies.slice(0, SHOWN);
  const exchanges = [
    summary.handsOver ? t('people.handsOver', { count: summary.handsOver }) : null,
    summary.takesOver ? t('people.takesOver', { count: summary.takesOver }) : null,
  ].filter(Boolean);

  return (
    <div className="grid max-w-[760px] gap-4">
      <section className="flex flex-wrap items-center gap-3 rounded-xl border border-[rgba(var(--accentColor),0.35)] bg-[rgba(var(--accentColor),0.08)] p-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[rgba(var(--accentColor),0.14)] text-[18px] text-accent">
          <TeamOutlined />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[11.5px] font-semibold text-zinc-600">
            {t('person.firms')}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[22px] font-extrabold leading-tight text-accent">
              {summary.firms ?? companies.length}
            </span>
            {summary.confirmed && (
              <Tag color="green" className="m-0">
                {t('confirm.state_CONFIRMED')}
              </Tag>
            )}
          </div>
          {exchanges.length > 0 && (
            <div className="mt-0.5 text-[12px] text-zinc-600">
              {exchanges.join(' · ')}
            </div>
          )}
        </div>
        <Link
          to={`${ROUTES.admin}/${ROUTES.teamAssignments}?person=${summary.employeeId}`}
        >
          <Button shape="round" type="primary" icon={<TeamOutlined />}>
            {t('person.openBoard')}
          </Button>
        </Link>
      </section>

      {FUNCTIONS.some((fn) => summary.byFunction?.[fn]) && (
        <div className="flex flex-wrap gap-1.5">
          {FUNCTIONS.filter((fn) => summary.byFunction?.[fn]).map((fn) => (
            <Tag key={fn} color={FUNCTION_COLORS[fn]} className="m-0">
              {t(`functions.${fn}`)}: {summary.byFunction[fn]}
            </Tag>
          ))}
        </div>
      )}

      {list.length > 0 && (
        <section className="rounded-xl border border-zinc-200 bg-white">
          {list.map((company) => (
            <Link
              key={company.companyId}
              to={`${ROUTES.admin}/${ROUTES.companies}/${company.companyId}`}
              className="block border-t border-zinc-100 px-3 py-2 text-[12.5px] font-semibold text-zinc-800 first:border-t-0 hover:bg-zinc-50 hover:text-accent"
            >
              {company.name}
            </Link>
          ))}
          {!all && companies.length > SHOWN && (
            <button
              type="button"
              className="block w-full border-t border-zinc-100 px-3 py-2 text-left text-[12px] font-semibold text-accent hover:underline"
              onClick={() => setAll(true)}
            >
              {t('person.more', { count: companies.length - SHOWN })}
            </button>
          )}
        </section>
      )}
    </div>
  );
}
